/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { path } from 'ramda';
import React from 'react';
import clsx from 'clsx';
import Error from '../Error';
import Loader from '../Loader';
import { TableData } from './TableData';
import { TableHeader } from './TableHeader';
import { TableRow } from './TableRow';

export default function Table<T>({
    data,
    loading,
    error,
    columns,
    onClick,
    getStatus,
    renderExpanded,
    onRowContextMenu,
    dense,
    fitted,
}: {
    data?: T[];
    columns?: {
        title: string;
        key: keyof T | string;
        width?: string;
        /** Share of the table width, used when the table is . */
        widthPercent?: number;
        renderHeader?: (rows: T[]) => React.ReactNode;
        render?: (row: T) => React.ReactNode;
        onClick?: (row: T) => void;
    }[];
    onClick?: (row: T) => void;
    getStatus?: (row: T) => string;
    loading?: boolean | string;
    error?: boolean | string;
    /**
     * Optional detail shown directly beneath a row. Return null or undefined
     * for rows that have nothing to expand. Optional, so tables that do not
     * use it are unaffected.
     */
    renderExpanded?: (row: T) => React.ReactNode;
    /**
     * Right-click on a row. The caller is responsible for calling
     * preventDefault if it wants to replace the browser menu. Optional, so
     * tables that do not use it keep the native menu.
     */
    onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
    /**
     * Tighter cell padding for tables with many columns, which otherwise
     * need horizontal scrolling. Optional, so every other table is unchanged.
     */
    dense?: boolean;
    /**
     * Divide the available width between the columns instead of sizing them
     * to their content, so a wide table fits its container rather than
     * scrolling sideways. Values that do not fit truncate. Optional, so every
     * other table keeps its current behaviour.
     */
    fitted?: boolean;
}) {
    const cellValue = (row: T, column: NonNullable<typeof columns>[number]) => {
        if (column.render) return column.render(row);
        return path((column.key as string).split('.'), row) as string;
    };

    const content = () => {
        if (loading) {
            return (
                <tr>
                    <td className="p-6" colSpan={columns?.length}>
                        <Loader message={loading !== true && loading} />
                    </td>
                </tr>
            );
        }
        if (error) {
            return (
                <tr>
                    <td className="p-6" colSpan={columns?.length}>
                        <Error message={error !== true && error} />
                    </td>
                </tr>
            );
        }
        if (data && data.length === 0) {
            return (
                <tr>
                    <td
                        className="p-6 text-sm text-center text-foreground-muted"
                        colSpan={columns?.length}
                    >
                        No results found
                    </td>
                </tr>
            );
        }
        if (data) {
            return data.map((row, i) => {
                const expanded = renderExpanded ? renderExpanded(row) : null;

                return (
                    <React.Fragment key={i}>
                        <TableRow
                            hover
                            onClick={(() => {
                                if (!onClick) return undefined;
                                return () => onClick(row);
                            })()}
                            onContextMenu={(() => {
                                if (!onRowContextMenu) return undefined;
                                return (event: React.MouseEvent) => onRowContextMenu(row, event);
                            })()}
                            status={getStatus ? getStatus(row) : undefined}
                        >
                            {columns?.map((column) => (
                                <TableData
                                    key={column.key as string}
                                    dense={dense}
                                    onClick={(() => {
                                        const onColumnClick = column.onClick;
                                        if (!onColumnClick) return undefined;
                                        return () => onColumnClick(row);
                                    })()}
                                >
                                    {cellValue(row, column)}
                                </TableData>
                            ))}
                        </TableRow>
                        {expanded && (
                            <tr className="border-b border-border-subtle last:border-b-0">
                                <td className="p-0" colSpan={columns?.length}>
                                    {expanded}
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                );
            });
        }
        return null;
    };

    const renderMobileCards = () => {
        if (loading) {
            return (
                <div className="p-6">
                    <Loader message={loading !== true ? loading : undefined} />
                </div>
            );
        }
        if (error) {
            return (
                <div className="p-6">
                    <Error message={error !== true ? error : undefined} />
                </div>
            );
        }
        if (data && data.length === 0) {
            return (
                <div className="p-6 text-sm text-center text-foreground-muted">
                    No results found
                </div>
            );
        }
        if (data && columns) {
            const [primaryColumn, ...restColumns] = columns;
            return data.map((row, i) => (
                <div
                    key={i}
                    className={clsx(
                        'p-4 border-b border-border-subtle last:border-b-0',
                        onClick && 'cursor-pointer active:bg-surface-muted'
                    )}
                    onClick={onClick ? () => onClick(row) : undefined}
                >
                    {primaryColumn && (
                        <div className="text-sm font-semibold text-foreground mb-2">
                            {cellValue(row, primaryColumn)}
                        </div>
                    )}
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                        {restColumns.map((column) => (
                            <div key={column.key as string}>
                                <dt className="text-xs text-foreground-muted">{column.title}</dt>
                                <dd className="text-sm text-foreground mt-0.5">
                                    {cellValue(row, column)}
                                </dd>
                            </div>
                        ))}
                    </dl>
                    {renderExpanded ? renderExpanded(row) : null}
                </div>
            ));
        }
        return null;
    };

    return (
        <>
            <div className="hidden md:block bg-surface border border-border rounded-xl overflow-x-auto overflow-y-hidden">
                <table className={clsx('w-full border-collapse', fitted && 'table-fixed')}>
                    <thead>
                        <TableRow>
                            {columns?.map((column) => (
                                <TableHeader
                                    key={column.key as string}
                                    width={column.width}
                                    widthPercent={column.widthPercent}
                                    fitted={fitted}
                                    dense={dense}
                                >
                                    {(() => {
                                        if (column.renderHeader)
                                            return column.renderHeader(data || []);
                                        return column.title;
                                    })()}
                                </TableHeader>
                            ))}
                        </TableRow>
                    </thead>
                    <tbody>{content()}</tbody>
                </table>
            </div>
            <div className="md:hidden bg-surface border border-border rounded-xl overflow-hidden">
                {renderMobileCards()}
            </div>
        </>
    );
}
